import classNames from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import { RadioSwitch } from 'tradera/components/form-controls/radio-switch';

export type ContentSwitchTab = {
    label: string;
    node: React.ReactNode;
    key: string;
};

type TitleProp = string | React.ReactNode | ((key: string) => React.ReactNode);

type ContentSwitchProps = {
    defaultSelected?: string;
    onSwitch?: (selectedLabel: string) => void;
    options: ContentSwitchTab[];
    title?: TitleProp;
    id?: string;
    className?: string;
    hideTabSelector?: boolean;
    /**
     * To make ContentSwitch a controlled component
     */
    selectedTab?: string;
};

/**
 * This component can act as both controlled and uncontrolled. By passing in
 * selectedTab it becomes controlled, otherwise it's uncontrolled and only
 * defaultSelected tab can be specified.
 */
export const ContentSwitch = ({
    className,
    defaultSelected,
    selectedTab: selectedTabProp,
    onSwitch,
    options,
    title: titleProp,
    hideTabSelector,
    id
}: ContentSwitchProps) => {
    const [selected, setSelected] = useState(defaultSelected || options[0].key);
    const selectedTab = selectedTabProp ?? selected;
    const uniqueId = useRef(id ?? `radio-switch-${Math.random().toString(36)}`);

    // Keep track of which options have been shown to only render what's needed
    const [renderedOptions, setRenderedOptions] = useState([selectedTab]);
    const onChange = useCallback(
        (tab: string) => {
            if (!renderedOptions.includes(tab)) {
                setRenderedOptions([...renderedOptions, tab]);
            }
            setSelected(tab);
            onSwitch?.(tab);
        },
        [onSwitch, renderedOptions, setRenderedOptions, setSelected]
    );

    const title = getTitle(titleProp, selected);
    return (
        <section className={className}>
            <div
                className={classNames('mb-2 mb-md-3', {
                    'mx-2 mx-md-3 mx-lg-2 mx-xl-0 text-styled': title,
                    'ml-lg-3 ml-xl-2': !title
                })}>
                {title}
                {!hideTabSelector && (
                    <div className="text-nowrap">
                        <RadioSwitch
                            id={uniqueId.current}
                            options={options.map((option) => ({
                                value: option.key,
                                name: option.label
                            }))}
                            defaultChecked={selectedTab}
                            handleOnChange={onChange}
                            dynamic
                            style={{
                                minWidth: 'min(100%,25rem)',
                                maxWidth: '100%'
                            }}
                        />
                    </div>
                )}
            </div>
            {options.map((option) => (
                <div
                    key={option.key}
                    className={`${
                        option.key === selectedTab ? 'd-block' : 'd-none'
                    }`}>
                    {renderedOptions.includes(option.key) ? option.node : null}
                </div>
            ))}
        </section>
    );
};

function getTitle(
    title: TitleProp | undefined,
    key: string
): React.ReactNode | undefined {
    switch (typeof title) {
        case 'string':
            return <h3 className="mb-1">{title}</h3>;
        case 'object':
            return title;
        case 'function':
            return title(key);
        default:
            return undefined;
    }
}
